import { useSelector, useDispatch, shallowEqual } from "react-redux";
import type { RootState } from "@/store";
import { UnitCheckout } from "@/components/journey/scenes/UnitCheckout";
import { UnitFunnel } from "@/components/journey/scenes/UnitFunnel";
import { VbpModalContent } from "@glidevvr/se-components";
import "@glidevvr/se-components/style.css";
import { FunnelType, Step } from "@/types/enums";
import { useTriggerButtonVisibility } from "@/hooks/useTriggerButtonVisibility.tsx";
import { setSelectedPromo } from "@/features/journey/journeySlice";
import { useRentalAction } from "@/hooks/useRentalAction.tsx";
import { useReservationAction } from "@/hooks/useReservationAction.tsx";
import { useDisplayOptionContext } from "@/hooks/useDisplayOptionContext.ts";
import { useFacility } from "@/hooks/useFacility.tsx";
import { useUnitItemContext } from "@/hooks/useUnitItemContext";

export const JourneyContent = () => {
    const dispatch = useDispatch();
    const options = useDisplayOptionContext();

    // Get unit data from context (provided by UnitJourneyWrapper)
    const { unitGroup, selectedPromo } = useUnitItemContext();

    // Get journey navigation state from Redux
    const { step, selectedUnitId } = useSelector(
        (s: RootState) => ({
            step: s.journey.step,
            selectedUnitId: s.journey.selectedUnitId,
        }),
        shallowEqual,
    );

    const { openRentalFunnel } = useRentalAction(unitGroup, selectedPromo);
    const { openReservationFunnel } = useReservationAction(
        unitGroup,
        selectedPromo,
    );
    const { canReserve, canRent } = useTriggerButtonVisibility(unitGroup);
    const { facility } = useFacility(unitGroup.representative_unit.facility_id);

    const hideReservationButtonOnVbpModal =
        facility?.settings.hide_reservation_button_on_vbp_modal || false;

    const JourneyStepContent = () => {
        switch (step) {
            case Step.VBP:
                return (
                    <VbpModalContent
                        unitGroup={unitGroup}
                        disableRental={!canRent}
                        disableReservation={
                            !canReserve || hideReservationButtonOnVbpModal
                        }
                        disableSoftReservation={true}
                        onPromoChange={(promoId) =>
                            dispatch(setSelectedPromo(Number(promoId)))
                        }
                        selectedPromo={selectedPromo}
                        onRentalClick={openRentalFunnel}
                        onReservationClick={openReservationFunnel}
                        vacancyNotice={options.vacancyNotice}
                        vacancyThreshold={options.vacancyThreshold}
                        removeCents={facility?.settings?.remove_cents_from_rate}
                    />
                );

            case Step.RENT_RESERVE_QUOTE:
                return <UnitCheckout />;

            case Step.FUNNEL:
                return (
                    <UnitFunnel
                        type={FunnelType.RESERVE}
                        selectedUnitId={selectedUnitId}
                    />
                );

            default:
                return null;
        }
    };

    return (
        <div className="journey-content">
            <JourneyStepContent />
        </div>
    );
};
